import React, { useState } from 'react';
import { Link, generatePath, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import ToDoAlertCard from './ToDoAlertCard';
import { receiveRemoveSiteTodo, confirmSiteTodo, removeSiteTodo } from '../../../actions/sites';
import snackbarHelper from '../../../helpers/snackbarHelper';
import OverlayLoader from '../../../components/OverlayLoader';

export const SUBSTITUTION_RESCUES = 'Substitution Hotlist';

const containerSx = {
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  paddingLeft: 2,
  paddingRight: 2,
};

const SDToDoToDoSubstitutionRescuesBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={routes.mobileToDo} label="To-do" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="Substitution Hotlist" />
    </Breadcrumbs>
  </Box>
);

const ToDoSubstitutionRescues = () => {
  const location = useLocation();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const [currentCardIndex, setCurrentCardIndex] = useState(location.state?.toDoCardIndex || 0);
  const [loading, setLoading] = useState();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const substitutionRescues = sdTodos?.substitutionRescues || [];

  const handleSkipClick = () => {
    setCurrentCardIndex(currentCardIndex + 1);
  };

  const handleRemoveClick = (todo, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() => dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false)))
      .then(() => {
        handleCardOut();
        // Wait for animation to complete before removing todo from the store
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const handleConfirmClick = (todo, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() => dispatch(confirmSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false)))
      .then(() => {
        handleCardOut();
        // Wait for animation to complete before removing todo from the store
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  // Show if there is no more pickups to review or when the last card has been skipped
  if (!substitutionRescues.length || currentCardIndex + 1 > substitutionRescues.length) {
    return (
      <>
        <SDToDoToDoSubstitutionRescuesBreadcrumbs />
        <Box sx={containerSx}>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              WebkitTransform: 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You’re all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <OverlayLoader loading={loading}>
      <SDToDoToDoSubstitutionRescuesBreadcrumbs />

      <Box sx={containerSx}>
        <Box sx={{ position: 'relative', height: '100%' }}>
          {substitutionRescues.map((rescue, index) => (
            <ToDoAlertCard
              key={rescue.entity_id}
              rescue={rescue}
              current={index === currentCardIndex}
              next={substitutionRescues.length <= index ? null : index === currentCardIndex + 1}
              onRemoveClick={handleRemoveClick}
              onSkipClick={handleSkipClick}
              onReviewClick={handleConfirmClick}
              reviewLabel="Confirm"
              content={
                <span>
                  Pickup from{' '}
                  <b>
                    {rescue?.details?.location?.length <= 20
                      ? rescue?.details?.location
                      : `${rescue?.details?.location.substr(0, 20)}...`}
                  </b>{' '}
                  to{' '}
                  <b>
                    {rescue?.details?.receiver?.length <= 20
                      ? rescue?.details?.receiver
                      : `${rescue?.details?.receiver?.substr(0, 20)}...`}
                  </b>{' '}
                  on <b>{moment(rescue?.details?.date).format('ddd MMM Do')}</b> from{' '}
                  <b>{moment(rescue?.details?.pickup_begin, 'HH:mm:ss').format('hh:mma')}</b> to{' '}
                  <b>{moment(rescue?.details?.pickup_end, 'HH:mm:ss').format('hh:mma')}</b> is assigned to{' '}
                  <b>{rescue?.details?.claimer}</b>, and may need a substitution.
                  Click Confirm to send a confirmation, or remove alert if you&apos;ve confirmed that no substitution is
                  needed.
                </span>
              }
            />
          ))}
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default ToDoSubstitutionRescues;
